import { EducatorPageChrome } from "@/pages/educators/page-chrome"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { PageBody } from "@/components/ui/layout"
import {
  Brain,
  HeartHandshake,
  LucideIcon,
  NotebookText,
  Settings2,
  Users,
} from "lucide-react"
import { useTranslation } from "react-i18next"
import { Resources } from "i18next"
import { useTObject, useTOptions } from "@/i18n"
import React from "react"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { camelCase } from "lodash-es"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { SharedFacadesStudent } from "@/types/serializers"
import { useProps } from "@/lib/page-rendering"

type ShowProps = { student: SharedFacadesStudent; class_name: string }
const useShowProps = () => useProps<ShowProps>()

export default function Page() {
  const { class_name } = useShowProps()

  return (
    <>
      <EducatorPageChrome records={{ "educators/classes/edit": class_name }} />
      <PageBody>
        <Card>
          <CardContent className="grid h-full w-full grid-cols-1 gap-6 p-6 lg:grid-cols-[2.5fr_3fr_3fr]">
            <StudentInfo />
            <div className="flex flex-col gap-6">
              <StudentAssessmentSection />
              <LearningStyleSection />
              <LearningPreferenceSection />
            </div>

            <div className="flex flex-col gap-6">
              <ParentInfo />
              <OtherInfo />
            </div>
          </CardContent>
        </Card>
      </PageBody>
    </>
  )
}

function StudentInfo() {
  const { student } = useShowProps()
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-4">
        <Avatar className="size-45">
          <AvatarFallback>
            {student.firstName[0]}
            {student.lastName[0]}
          </AvatarFallback>
        </Avatar>
        <span className="text-2xl font-semibold">
          {student.firstName} {student.lastName}
        </span>
        <span>@{student.schoolName}</span>
        <BasicInfo />
        {/*<div className="flex flex-row justify-between gap-2">
            <MapPin />
            {student.stateOfResidence?.label ? student.stateOfResidence?.label + ', ' : null}
            {student.countryOfResidence?.label ? student.countryOfResidence?.label : 'N/A'}
          </div>*/}
      </CardContent>
    </Card>
  )
}

function BasicInfo() {
  const { t } = useTranslation("students", { keyPrefix: "show" })
  const {
    student: { user },
  } = useShowProps()
  const { t: tLanguages } = useTranslation("languages", {
    keyPrefix: "languages",
  })
  const { t: tGenders } = useTranslation("common", { keyPrefix: "user.gender" })
  const { t: tCountries } = useTranslation("countries", {
    keyPrefix: "by_sacc_code",
  })
  const { t: tReligions } = useTranslation("religions", {
    keyPrefix: "religions",
  })
  const { t: tRaces } = useTranslation("races", {
    keyPrefix: "cultural_ethnic_groups",
  })

  return (
    <div className="flex w-full flex-col gap-4">
      <Attribute label={t("dob")} value={user.dob ?? t("missing_attribute")} />
      <Attribute
        label={t("primary_language")}
        value={
          user.primaryLanguageKey
            ? tLanguages(
                user.primaryLanguageKey as keyof Resources["languages"]["languages"],
              )
            : t("missing_attribute")
        }
      />
      <Attribute
        label={t("secondary_language")}
        value={
          user.secondaryLanguageKey
            ? tLanguages(
                user.secondaryLanguageKey as keyof Resources["languages"]["languages"],
              )
            : t("missing_attribute")
        }
      />
      <Attribute
        label={t("gender")}
        value={
          user.genderKey
            ? tGenders(
                user.genderKey as keyof Resources["common"]["user"]["gender"],
              )
            : t("missing_attribute")
        }
      />

      <Attribute
        label={t("citizenships")}
        value={
          user.citizenshipCountryKeys.length > 0
            ? user.citizenshipCountryKeys.map((saccCode) =>
                tCountries(
                  saccCode as keyof Resources["countries"]["by_sacc_code"],
                ),
              )
            : t("missing_attribute")
        }
      />

      <Attribute
        label={t("origin_country")}
        value={
          user.originCountryKey
            ? tCountries(
                user.originCountryKey as keyof Resources["countries"]["by_sacc_code"],
              )
            : t("missing_attribute")
        }
      />

      <Attribute
        label={t("religion")}
        value={
          user.religionKey
            ? tReligions(
                user.religionKey as keyof Resources["religions"]["religions"],
              )
            : t("missing_attribute")
        }
      />

      <Attribute
        label={t("race")}
        value={
          user.raceKey
            ? tRaces(
                user.raceKey as keyof Resources["races"]["cultural_ethnic_groups"],
              )
            : t("missing_attribute")
        }
      />
    </div>
  )
}

function Attribute({
  label,
  value,
}: {
  label: string
  value: string | string[]
}) {
  return (
    <div className="flex flex-row flex-wrap justify-between">
      <div className="text-muted-foreground">{label}</div>
      <div>{Array.isArray(value) ? value.join(", ") : value}</div>
    </div>
  )
}

function IconCardTitle({
  children,
  Icon,
}: {
  children: React.ReactNode
  Icon: LucideIcon
}) {
  return (
    <CardTitle className="flex flex-row gap-2">
      <Icon />
      {children}
    </CardTitle>
  )
}

function pickByPrefix<T extends object, P extends string>(
  obj: T | null | undefined,
  prefix: P,
): Pick<T, Extract<keyof T, `${P}${string}`>> {
  if (!obj) return {} as Pick<T, Extract<keyof T, `${P}${string}`>>
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => key.startsWith(prefix)),
  ) as Pick<T, Extract<keyof T, `${P}${string}`>>
}

function LearningStyleSection() {
  const { student } = useShowProps()
  const studentLearningStyles = pickByPrefix(student, "learningStyle")

  const { t } = useTranslation(["students", "classes"])
  const { t: tExtentOptions } = useTranslation("student_attributes_options", {
    keyPrefix: "extent_options",
  })

  const learningStyles = {
    vark: useTOptions("form.learning_style_section.vark", "students").map(
      (option) => ({
        ...option,
        value: camelCase(option.value.replace("student.", "")),
      }),
    ),
    gardners_multiple_intelligences: useTOptions(
      "form.learning_style_section.gardners_multiple_intelligences",
      "students",
    ).map((option) => ({
      ...option,
      value: camelCase(option.value.replace("student.", "")),
    })),
    kolbs_experiential_learning_theory: useTOptions(
      "form.learning_style_section.kolbs_experiential_learning_theory",
      "students",
    ).map((option) => ({
      ...option,
      value: camelCase(option.value.replace("student.", "")),
    })),
  }
  return (
    <Card>
      <CardHeader>
        <IconCardTitle Icon={Brain}>
          {t("show.learning_styles.title")}
        </IconCardTitle>
      </CardHeader>
      <CardContent>
        <Accordion type="multiple">
          <AccordionItem value="description">
            <AccordionTrigger>
              {t("show.learning_styles.description")}
            </AccordionTrigger>
            <AccordionContent>
              {studentLearningStyles.learningStyleDescription ??
                t("show.missing_attribute")}
            </AccordionContent>
          </AccordionItem>
          {[
            "vark",
            "gardners_multiple_intelligences",
            "kolbs_experiential_learning_theory",
          ].map((learningStyle) => (
            <AccordionItem key={learningStyle} value={learningStyle}>
              <AccordionTrigger>
                {t(
                  `show.learning_styles.subsections.${learningStyle as keyof Resources["students"]["show"]["learning_styles"]["subsections"]}`,
                )}
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-[3fr_1fr] gap-y-2">
                  {learningStyles[
                    learningStyle as keyof typeof learningStyles
                  ].map((option) => (
                    <React.Fragment key={option.value}>
                      <div>{option.label}</div>
                      <div>
                        {studentLearningStyles[
                          option.value as keyof typeof studentLearningStyles
                        ]
                          ? tExtentOptions(
                              studentLearningStyles[
                                option.value as keyof typeof studentLearningStyles
                              ]! as keyof Resources["student_attributes_options"]["extent_options"],
                            )
                          : t("show.missing_attribute")}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </CardContent>
    </Card>
  )
}

function LearningPreferenceSection() {
  const { student } = useShowProps()

  const studentLearningPreferences = pickByPrefix(student, "learningPreference")

  const { t } = useTranslation(["students", "common"])
  const fields = useTOptions(
    "form.learning_preference_section",
    "students",
  ).filter((field) => field.value.startsWith("learning_preference"))

  const displayValue = (field: string) => {
    const value =
      studentLearningPreferences[
        camelCase(field) as keyof typeof studentLearningPreferences
      ]

    if (typeof value === "boolean") {
      return t(`common:boolean.${value}`)
    }
    if (!value) {
      return t("show.missing_attribute")
    }
    return value
  }

  return (
    <Card>
      <CardHeader>
        <IconCardTitle Icon={Settings2}>
          {t("show.learning_preference.title")}
        </IconCardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-[3fr_1fr] gap-y-2 text-sm">
          {fields.map((field) => (
            <React.Fragment key={field.value}>
              <div> {field.label}</div>
              <div>{displayValue(field.value)}</div>
            </React.Fragment>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function Naplan() {
  const {
    student: { ability },
  } = useShowProps()
  const { t } = useTranslation(["students", "classes"])
  const { t: tNaplan } = useTranslation("student_attributes_options", {
    keyPrefix: "naplan_options",
  })
  const naplanAttributes = useTOptions("show.naplan", "students")

  return (
    <div className="grid grid-cols-2">
      {naplanAttributes.map(({ label, value }) => (
        <React.Fragment key={value}>
          <div>{label}</div>
          <div>
            {ability && ability[camelCase(value) as keyof typeof ability]
              ? tNaplan(
                  ability[
                    camelCase(value) as keyof typeof ability
                  ] as keyof Resources["student_attributes_options"]["naplan_options"],
                )
              : t("show.missing_attribute")}
          </div>
        </React.Fragment>
      ))}
    </div>
  )
}

function ParentInfo() {
  const { student } = useShowProps()
  const parentInfo = useTOptions("form.parent_information_section", "students")
  const tOptions = {
    ...useTObject("parent_education_options", "student_attributes_options"),
    ...useTObject("parent_occupation_options", "student_attributes_options"),
  }
  const { t } = useTranslation(["students", "classes"])
  return (
    <Card>
      <CardHeader>
        <IconCardTitle Icon={Users}>
          {t("show.parent_information")}
        </IconCardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-x-4 gap-y-4 text-sm">
        {parentInfo.map(({ label, value }) => (
          <React.Fragment key={value}>
            <div>{label}:</div>
            <div>
              {student[camelCase(value) as keyof typeof student]
                ? tOptions[
                    student[
                      camelCase(value) as keyof typeof student
                    ] as keyof typeof tOptions
                  ]
                : t("show.missing_attribute")}
            </div>
          </React.Fragment>
        ))}
      </CardContent>
    </Card>
  )
}

function OtherInfo() {
  const {
    student: { otherInformation },
  } = useShowProps()
  const { t } = useTranslation(["students", "common"])
  const { t: tOptions } = useTranslation("student_attributes_options")
  const attributes: Array<
    keyof Resources["students"]["show"]["other_information"]["subsections"]
  > = ["adhd", "autistic", "eyesight", "depression"]
  if (!otherInformation) return null

  const optionsLookup: Record<
    keyof Resources["students"]["show"]["other_information"]["subsections"],
    keyof Resources["student_attributes_options"]
  > = {
    adhd: "adhd_options",
    autistic: "autism_options",
    eyesight: "visual_impairment_options",
    depression: "depression_options",
  }

  return (
    <Card>
      <CardHeader>
        <IconCardTitle Icon={HeartHandshake}>
          {t("show.other_information.title")}
        </IconCardTitle>
      </CardHeader>
      <CardContent>
        <Accordion type="multiple" className="w-full">
          <AccordionItem value="other_comment">
            <AccordionTrigger>{t("show.comments")}</AccordionTrigger>
            <AccordionContent>
              {otherInformation.otherComment ?? t("show.missing_attribute")}
            </AccordionContent>
          </AccordionItem>
          {attributes.map((attr) => (
            <AccordionItem key={attr} value={attr}>
              <AccordionTrigger>
                {t(`show.other_information.subsections.${attr}`)}
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid grid-cols-2 gap-y-2">
                  <div>{t(`show.other_information.subsections.${attr}`)}</div>
                  <div>
                    {otherInformation[attr]
                      ? tOptions(
                          `${optionsLookup[attr]}.${otherInformation[attr]}` as any,
                        )
                      : t("show.missing_attribute")}
                  </div>
                  <div>
                    {t("form.other_information_section.professional_diagnosis")}
                  </div>
                  <div>
                    {typeof otherInformation[`${attr}ProfessionalDiagnosis`] ===
                    "boolean"
                      ? t(
                          `common:boolean.${otherInformation[`${attr}ProfessionalDiagnosis`]}`,
                        )
                      : t("show.missing_attribute")}
                  </div>
                  <div>{t("show.comments")}</div>
                  <div>
                    {otherInformation[`${attr}Comment`]
                      ? otherInformation[`${attr}Comment`]
                      : t("show.missing_attribute")}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </CardContent>
    </Card>
  )
}

function Wais4() {
  const { student } = useShowProps()
  const { t } = useTranslation(["students", "classes"])
  const waisIV = pickByPrefix(student.ability, "waisIV")

  const fields = useTOptions("form.ability_section.wais_iv", "students")
    .filter((field) => field.value.endsWith("_index"))
    .map((field) => ({
      ...field,
      value: camelCase(field.value).replace("waisIv", "waisIV"),
    }))
  if (!waisIV) return null

  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
      {fields.map((field) => (
        <React.Fragment key={field.value}>
          <div>{field.label}</div>
          <div className="flex gap-2">
            <div>
              {waisIV[field.value as keyof typeof waisIV] ??
                t("show.missing_attribute")}
            </div>
            <div className="text-muted-foreground">
              (
              {waisIV[
                field.value.replace(
                  "index",
                  "percentile_rank",
                ) as keyof typeof waisIV
              ] ?? t("show.missing_attribute")}
              %)
            </div>
          </div>
        </React.Fragment>
      ))}
    </div>
  )
}

function Wisc5() {
  const { student } = useShowProps()
  const { t } = useTranslation(["students", "classes"])
  const wiscV = student.ability?.wiscV
  const fields = useTOptions("form.ability_section.wisc_v", "students").filter(
    (field) => field.value.endsWith("_score"),
  )
  const detailFields = useTOptions(
    "form.ability_section.wisc_v",
    "students",
  ).filter(
    (field) =>
      !field.value.endsWith("_score") &&
      !field.value.endsWith("_percentile") &&
      field.value !== "tooltips",
  )
  if (!wiscV) return null

  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
      {detailFields.map((field) => (
        <React.Fragment key={field.value}>
          <div>{field.label}</div>
          <div>
            {wiscV[camelCase(field.value) as keyof typeof wiscV] ??
              t("show.missing_attribute")}
          </div>
        </React.Fragment>
      ))}

      {fields.map((field) => (
        <React.Fragment key={field.value}>
          <div>{field.label}</div>
          <div className="flex gap-2">
            <div>
              {wiscV[camelCase(field.value) as keyof typeof wiscV] ??
                t("show.missing_attribute")}
            </div>
            <div className="text-muted-foreground">
              (
              {wiscV[
                camelCase(
                  field.value.replace("score", "percentile"),
                ) as keyof typeof wiscV
              ] ?? t("show.missing_attribute")}
              %)
            </div>
          </div>
        </React.Fragment>
      ))}
    </div>
  )
}

function Gat() {
  const { student } = useShowProps()
  const { t } = useTranslation(["students", "common"])
  const gat = student.ability?.gat
  const fields = useTOptions("form.ability_section.gat", "students")

  if (!gat) return null

  const displayGatValue = (field: string) => {
    const gatValue = gat[camelCase(field) as keyof typeof gat]

    if (gatValue === null) {
      return t("show.missing_attribute")
    }

    if (typeof gatValue === "boolean") {
      return t(`common:boolean.${gatValue}`)
    }

    return gatValue
  }

  return (
    <div className="grid grid-cols-[2fr_1fr] gap-x-4 gap-y-4">
      {fields.map((field) => (
        <React.Fragment key={field.value}>
          <div>{field.label}</div>
          <div>{displayGatValue(field.value)}</div>
        </React.Fragment>
      ))}
    </div>
  )
}

function WiatIV() {
  const { student } = useShowProps()
  const { t } = useTranslation(["students", "classes"])
  const wiatIV = student.ability?.wiatIV
  const fields = useTOptions("form.ability_section.wiat_iv", "students").filter(
    (field) => field.value.endsWith("_score"),
  )
  const detailFields = useTOptions(
    "form.ability_section.wiat_iv",
    "students",
  ).filter(
    (field) =>
      !field.value.endsWith("_score") &&
      !field.value.endsWith("_percentile") &&
      field.value !== "tooltips",
  )
  if (!wiatIV) return null

  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
      {detailFields.map((field) => (
        <React.Fragment key={field.value}>
          <div>{field.label}</div>
          <div>
            {wiatIV[camelCase(field.value) as keyof typeof wiatIV] ??
              t("show.missing_attribute")}
          </div>
        </React.Fragment>
      ))}

      {fields.map((field) => (
        <React.Fragment key={field.value}>
          <div>{field.label}</div>
          <div className="flex gap-2">
            <div>
              {wiatIV[camelCase(field.value) as keyof typeof wiatIV] ??
                t("show.missing_attribute")}
            </div>
            <div className="text-muted-foreground">
              (
              {wiatIV[
                camelCase(
                  field.value.replace("score", "percentile"),
                ) as keyof typeof wiatIV
              ] ?? t("show.missing_attribute")}
              %)
            </div>
          </div>
        </React.Fragment>
      ))}
    </div>
  )
}

function StudentAssessmentSection() {
  const { t } = useTranslation(["students", "classes"])
  const fields: Array<
    [
      keyof Resources["students"]["show"]["student_assessment"]["subsections"],
      React.ComponentType,
    ]
  > = [
    ["naplan", Naplan],
    ["wiscV", Wisc5],
    ["waisIV", Wais4],
    ["wiatIV", WiatIV],
    ["gat", Gat],
    ["aeas", AEASSection],
    ["qualitativeAssessment", OverallQualitiativeAssessment],
    ["interestedSubjects", InterestedSubjects],
  ]

  return (
    <Card>
      <CardHeader>
        <IconCardTitle Icon={NotebookText}>
          {t("show.student_assessment.title")}
        </IconCardTitle>
      </CardHeader>
      <CardContent>
        <Accordion type="multiple">
          {fields.map(([field, Component]) => (
            <AccordionItem value={field} key={field}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <AccordionTrigger>
                    {t(`show.student_assessment.subsections.${field}`)}
                  </AccordionTrigger>
                </TooltipTrigger>
                <TooltipContent align="start">
                  {t(`show.student_assessment.tooltips.${field}`)}
                </TooltipContent>
              </Tooltip>
              <AccordionContent>
                <Component />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </CardContent>
    </Card>
  )
}

function AEASSection() {
  const { student } = useShowProps()
  const { t } = useTranslation(["students", "classes"])
  const aeas = pickByPrefix(student.ability, "aeas")
  const fields = useTOptions("form.ability_section.aeas", "students").filter(
    (field) => field.value.startsWith("aeas_"),
  )

  if (!aeas) return null

  return (
    <div className="grid grid-cols-[2fr_1fr] gap-x-4 gap-y-4">
      {fields.map((field) => (
        <React.Fragment key={field.value}>
          <div>{field.label}</div>
          <div>
            {aeas[field.value as keyof typeof aeas] ??
              t("show.missing_attribute")}
          </div>
        </React.Fragment>
      ))}
    </div>
  )
}

function OverallQualitiativeAssessment() {
  const {
    student: { ability },
  } = useShowProps()
  const { t } = useTranslation(["students", "classes"])
  const { t: tGrades } = useTranslation("student_attributes_options", {
    keyPrefix: "grade_options",
  })

  if (!ability) return null

  return (
    <div className="grid grid-cols-2 gap-x-8 gap-y-2">
      <div>
        {t(
          "form.ability_section.school_qualitative_assessment.overall_qualitative_assessment",
        )}
        :
      </div>
      <div>
        {ability.overallQualitativeAssessment
          ? tGrades(ability.overallQualitativeAssessment)
          : t("show.missing_attribute")}
      </div>
      <div>
        {t(
          "form.ability_section.school_qualitative_assessment.overall_qualitative_assessment_comments",
        )}
        :
      </div>
      <div>
        {ability.qualitativeAssessmentComment ?? t("show.missing_attribute")}
      </div>
    </div>
  )
}

function InterestedSubjects() {
  const {
    student: { ability },
  } = useShowProps()
  const { t } = useTranslation(["students", "classes"])
  const { t: tGrades } = useTranslation("student_attributes_options", {
    keyPrefix: "grade_options",
  })
  const { t: tSubjects } = useTranslation("subjects", { keyPrefix: "subjects" })

  if (!ability) return null

  return (
    <div className="grid grid-cols-2 gap-x-8 gap-y-2">
      {ability.interestedSubjects.length > 0 ? (
        ability.interestedSubjects.map(
          ({ subjectKey, qualitativeAssessment }) => (
            <React.Fragment key={subjectKey}>
              <div>
                {tSubjects(
                  subjectKey as keyof Resources["subjects"]["subjects"],
                )}
              </div>
              <div>
                {qualitativeAssessment
                  ? tGrades(qualitativeAssessment)
                  : t("show.missing_attribute")}
              </div>
            </React.Fragment>
          ),
        )
      ) : (
        <div>{t("show.student_assessment.interested_subjects_empty")}</div>
      )}
    </div>
  )
}
